/**
 * SessionsList — Sessions section on `/users/:userId`.
 *
 * Tier 0 (slice-local). Renders the BE-driven sessions list, per-row
 * revoke (with confirm dialog), and a footer "Revoke all" affordance.
 *
 * Permission gating is delegated to `<RequirePermission>` from
 * `shared/auth/`. The per-row Revoke button AND the "Revoke all"
 * button are both gated on `identity.users.sessions.revoke`. The
 * outer page already gates the section render on
 * `identity.users.sessions.read`.
 */

import * as React from 'react';
import { Button } from '@sparta/ui-kit';
import { RequirePermission } from '../../../shared/auth/RequirePermission.js';
import { useUserSessions } from '../api/use-user-sessions.js';
import { useRevokeUserSession } from '../api/use-revoke-user-session.js';
import type { UserSessionRow } from '../api/types.js';

interface SessionsListProps {
  readonly userId: string;
}

function formatTimestamp(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function abbreviateSid(sid: string): string {
  if (sid.length <= 12) return sid;
  return `${sid.slice(0, 8)}…${sid.slice(-4)}`;
}

type ConfirmState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'one'; readonly sid: string }
  | { readonly kind: 'all' };

export function SessionsList({ userId }: SessionsListProps): React.ReactElement {
  const sessions = useUserSessions(userId);
  const revoke = useRevokeUserSession(userId);
  const [confirm, setConfirm] = React.useState<ConfirmState>({ kind: 'idle' });
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null);

  const closeConfirm = (): void => setConfirm({ kind: 'idle' });

  async function performRevokeOne(sid: string): Promise<void> {
    setErrorMessage(null);
    setStatusMessage(null);
    try {
      await revoke.revokeOneAsync(sid);
      setStatusMessage('Session revoked.');
    } catch (e: unknown) {
      const err = e as { kind?: string };
      if (err.kind === 'forbidden') {
        setErrorMessage(
          'We can’t revoke that session — you may have lost the “sessions.revoke” permission, or this is your own current session.',
        );
      } else {
        setErrorMessage(
          'We couldn’t revoke that session. Try again in a moment.',
        );
      }
    } finally {
      closeConfirm();
    }
  }

  async function performRevokeAll(): Promise<void> {
    setErrorMessage(null);
    setStatusMessage(null);
    try {
      const count = await revoke.revokeAllAsync();
      setStatusMessage(
        count === 1 ? 'Revoked 1 session.' : `Revoked ${count} sessions.`,
      );
    } catch (e: unknown) {
      const err = e as { kind?: string };
      if (err.kind === 'forbidden') {
        setErrorMessage(
          'We can’t revoke sessions — you may have lost the “sessions.revoke” permission.',
        );
      } else {
        setErrorMessage(
          'We couldn’t revoke all sessions. Try again in a moment.',
        );
      }
    } finally {
      closeConfirm();
    }
  }

  if (sessions.status === 'loading') {
    return (
      <div
        aria-busy="true"
        aria-label="Loading sessions"
        className="text-sm text-[var(--fg-tertiary)]"
      >
        Loading sessions…
      </div>
    );
  }

  if (sessions.status === 'forbidden') {
    return (
      <div role="alert" className="text-sm text-[var(--fg-secondary)]">
        You don’t have permission to view this user’s active sessions.
      </div>
    );
  }

  if (sessions.status === 'notFound') {
    return (
      <div role="alert" className="text-sm text-[var(--fg-secondary)]">
        Sessions not found for this user.
      </div>
    );
  }

  if (sessions.status === 'error') {
    return (
      <div role="alert" className="flex flex-col gap-3 text-sm text-[var(--fg-secondary)]">
        <p>We couldn’t load the active sessions.</p>
        <div>
          <Button variant="secondary" size="sm" onClick={() => sessions.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const rows: readonly UserSessionRow[] = sessions.data;
  if (rows.length === 0) {
    return (
      <p className="text-sm text-[var(--fg-tertiary)]">
        No active sessions for this user.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {errorMessage !== null ? (
        <div role="alert" className="text-sm text-[var(--fg-secondary)]">
          {errorMessage}
        </div>
      ) : null}
      {statusMessage !== null ? (
        <div role="status" className="text-sm text-[var(--fg-secondary)]">
          {statusMessage}
        </div>
      ) : null}

      <table aria-label="Active sessions" className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-[var(--border-subtle)] text-[11px] font-bold uppercase tracking-wider text-[var(--fg-tertiary)]">
            <th scope="col" className="py-2 pr-4">Session</th>
            <th scope="col" className="py-2 pr-4">Created</th>
            <th scope="col" className="py-2 pr-4">Last seen</th>
            <th scope="col" className="py-2 pr-4" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.sid}
              className="border-b border-[var(--border-subtle)]"
            >
              <td className="py-2 pr-4 font-mono text-xs text-[var(--fg-primary)]">
                {abbreviateSid(row.sid)}
              </td>
              <td className="py-2 pr-4 text-[var(--fg-secondary)]">
                {formatTimestamp(row.createdAt)}
              </td>
              <td className="py-2 pr-4 text-[var(--fg-secondary)]">
                {formatTimestamp(row.lastSeenAt)}
              </td>
              <td className="py-2 pr-4">
                <RequirePermission slug="identity.users.sessions.revoke">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={revoke.isPendingOne}
                    onClick={() => setConfirm({ kind: 'one', sid: row.sid })}
                  >
                    Revoke
                  </Button>
                </RequirePermission>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <RequirePermission slug="identity.users.sessions.revoke">
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            disabled={revoke.isPendingAll}
            onClick={() => setConfirm({ kind: 'all' })}
          >
            Revoke all
          </Button>
        </div>
      </RequirePermission>

      {confirm.kind !== 'idle' ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="sessions-confirm-title"
          className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
        >
          <h3
            id="sessions-confirm-title"
            className="text-base font-semibold text-[var(--fg-primary)]"
          >
            {confirm.kind === 'one' ? 'Revoke this session?' : 'Revoke all sessions?'}
          </h3>
          <p className="mt-2">
            {confirm.kind === 'one'
              ? `This signs out the device using session "${abbreviateSid(confirm.sid)}". They’ll need to sign in again.`
              : 'This signs out every active device for this user. They’ll need to sign in again.'}
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={
                confirm.kind === 'one' ? revoke.isPendingOne : revoke.isPendingAll
              }
              onClick={() => {
                if (confirm.kind === 'one') {
                  void performRevokeOne(confirm.sid);
                } else {
                  void performRevokeAll();
                }
              }}
            >
              {confirm.kind === 'one' ? 'Confirm revoke' : 'Confirm revoke all'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
